import { updateProject } from "@/components/common/project-provider/update-project";
import { parseErrorMessage } from "@/errors/components/error-handling-context";
import { selectBackgroundTasks } from "@/store/background-tasks/background-tasks-selector";
import { removeBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import {
  addIElementsToStore,
  removeIElementFromStore,
} from "@/store/ielements-store-utilities";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  createIElementBase,
  IElement,
  IElementBase,
  IElementDateTimeMarkupField,
  IElementDropDownMarkupField,
  IElementType,
  IElementUserDirectoryMarkupField,
  ILabel,
  isIElementMarkupAssignee,
  isIElementMarkupDueTime,
  isIElementMarkupState,
  ISOTimeString,
  isValid,
} from "@faro-lotv/ielement-types";
import { IElementsRecord, selectRootId } from "@faro-lotv/project-source";
import {
  ApiResponseError,
  CreateIElement,
  Mutation,
  MutationTypes,
  ProjectApi,
  TaskResultStatus,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { DateTime } from "luxon";
import { useCallback } from "react";

/**
 * Convert a CreateIElement object into a proper IElement
 *
 * @param newElement The CreateIElement object from which an IElement must be created
 * @param rootId The id of the root IElement of the project
 * @param userId The id of the current user
 * @param date The date of creation of this iElement
 * @returns The IElement extending all the info in the input CreateIElement
 */
export function convertCreateIElementToIElement(
  newElement: CreateIElement<IElementBase>,
  rootId: GUID,
  userId: GUID,
  date: string,
): IElementBase {
  return {
    ...newElement,
    root_Id: rootId,
    createdBy: userId,
    createdAt: date,
    modifiedBy: userId,
    modifiedAt: date,
  };
}

/**
 * Fill the childrenIds property of a list of iElements
 *
 * @param newElements The iElements to add to the local store
 * @param iElements The list of the existing iElements
 * @returns The input IElements containing the proper parent-child information
 */
export function updateHierarchy(
  newElements: IElement[],
  iElements: IElementsRecord,
): IElement[] {
  const extendedElements = {
    ...iElements,
  };
  for (const element of newElements) {
    extendedElements[element.id] = element;
  }

  const changedElements: Record<GUID, IElement> = {};
  for (const element of newElements) {
    const extendedElement = extendedElements[element.id];
    if (!extendedElement) continue;

    changedElements[element.id] = extendedElement;
    if (!element.parentId) continue;

    const parentElement = extendedElements[element.parentId];
    if (!parentElement) continue;

    const childrenIds = parentElement.childrenIds
      ? [...parentElement.childrenIds]
      : [];
    if (childrenIds.includes(element.id)) continue;

    childrenIds.push(element.id);

    const changedElement = {
      ...parentElement,
      childrenIds,
    };
    extendedElements[element.parentId] = changedElement;
    changedElements[element.parentId] = changedElement;
  }
  return Object.values(changedElements).filter(isValid);
}

const MUTATION_FAILED_ERROR = 405;

/**
 * Apply the input mutations by using the async entry point of the Project API
 * and poll the backend to get the mutation status
 *
 * @param projectApi The client to the Project API
 * @param mutations The list of mutations to apply
 * @returns The promise waiting for the mutations to be fully processed by the backend
 */
export async function applyAsyncMutations(
  projectApi: ProjectApi,
  mutations: Mutation[],
): Promise<void> {
  try {
    const taskId = await projectApi.applyMutationsAsync(mutations);

    const POLLING_TIMEOUT = 1000;

    const result = new Promise<void>((resolve, reject) => {
      setTimeout(async function pollTaskStatus() {
        try {
          const taskStatus = await projectApi.getTaskStatus(taskId);
          switch (taskStatus.status) {
            case TaskResultStatus.Success: {
              resolve();
              break;
            }
            case TaskResultStatus.Failed: {
              const errorMessage = parseErrorMessage(
                new ApiResponseError(
                  MUTATION_FAILED_ERROR,
                  "Error",
                  taskStatus.resultData,
                ),
              );
              reject(errorMessage);
              break;
            }
            case TaskResultStatus.Running: {
              setTimeout(pollTaskStatus, POLLING_TIMEOUT);
              break;
            }
          }
        } catch (e) {
          reject(e);
        }
      }, POLLING_TIMEOUT);
    });
    return result;
  } catch (e) {
    return new Promise<void>((resolve, reject) => reject(e));
  }
}

/**
 * Compute all the iElements that will be added or modified by a specific mutation,
 * so that the store can be optimistically updated before mutation is completely
 * processed by the backend, and update the input list.
 *
 * @param mutation The mutation to check
 * @param rootId The id of the project root IElement
 * @param userId The id of the current user
 * @param date The date of creation of the mutation
 * @param iElements The iElements currently saved in the store
 * @param newlyCreatedTags The list of tags that will be created by the mutations, but are not yet stored in the root object
 * @returns The list of iElements that will be added or modified by the input mutation
 */
export function mutationToIElements(
  mutation: Mutation,
  rootId: GUID,
  userId: GUID,
  date: string,
  iElements: IElementsRecord,
  newlyCreatedTags: ILabel[],
): {
  addedIElements?: GUID[];
  removedIElements?: GUID[];
} {
  switch (mutation.type) {
    case MutationTypes.MutationAddMeasurePolygon:
    case MutationTypes.MutationAddAttachment:
    case MutationTypes.MutationAdd3dNode:
    case MutationTypes.MutationAddMarkupPolygon: {
      const newElement = convertCreateIElementToIElement(
        mutation.newElement,
        rootId,
        userId,
        date,
      );
      iElements[newElement.id] = newElement;

      const elements = [newElement.id];
      if (mutation.group) {
        const newGroup = convertCreateIElementToIElement(
          mutation.group,
          rootId,
          userId,
          date,
        );
        iElements[newGroup.id] = newGroup;
        elements.push(newGroup.id);
      }
      return { addedIElements: elements };
    }
    case MutationTypes.MutationAddLabel: {
      // The iElement should exist, if it does not, it may mean that he will be created
      // by another mutation executed together with this one.
      // Be sure that the creation mutation is processed before this one.
      const iElement = iElements[mutation.elementId];
      assert(iElement, "iElement should exist before adding a label to it");

      const label = [
        ...(iElements[rootId]?.labels ?? []),
        ...newlyCreatedTags,
      ].find((l) => l.id === mutation.labelId);
      assert(label, "Label should be found before being added to an iElement");

      iElements[mutation.elementId] = {
        ...iElement,
        labelIds: [...(iElement.labelIds ?? []), mutation.labelId],
        labels: [...(iElement.labels ?? []), label],
      };
      return { addedIElements: [iElement.id] };
    }
    case MutationTypes.MutationRemoveLabel: {
      const iElement =
        iElements[mutation.elementId] ??
        createIElementBase({
          id: mutation.elementId,
          createdBy: userId,
          createdAt: mutation.createdAt,
          name: "markup",
          type: IElementType.markup,
          rootId,
        });

      iElements[mutation.elementId] = {
        ...iElement,
        labelIds: (iElement.labelIds ?? []).filter(
          (l) => l !== mutation.labelId,
        ),
        labels: [...(iElement.labels ?? [])].filter(
          (l) => l.id !== mutation.labelId,
        ),
      };
      return { addedIElements: [iElement.id] };
    }
    case MutationTypes.MutationAddMarkup: {
      const newElement = convertCreateIElementToIElement(
        mutation.newElement,
        rootId,
        userId,
        date,
      );
      iElements[newElement.id] = newElement;

      const elements = [newElement.id];

      mutation.markupFields.forEach((field) => {
        const fieldIElement = convertCreateIElementToIElement(
          field,
          rootId,
          userId,
          date,
        );
        iElements[fieldIElement.id] = fieldIElement;
        elements.push(fieldIElement.id);
      });
      return { addedIElements: elements };
    }
    case MutationTypes.MutationAddMarkupField:
    case MutationTypes.MutationAddCamView: {
      const newElement = convertCreateIElementToIElement(
        mutation.newElement,
        rootId,
        userId,
        date,
      );
      iElements[newElement.id] = newElement;
      return { addedIElements: [newElement.id] };
    }
    case MutationTypes.MutationSetElementName: {
      if (!mutation.elementId) return {};

      const iElement = iElements[mutation.elementId];
      if (!iElement) return {};

      iElements[mutation.elementId] = {
        ...iElement,
        name: mutation.value,
      };

      return { addedIElements: [iElement.id] };
    }
    case MutationTypes.MutationSetElementDescription: {
      if (!mutation.elementId) return {};

      const iElement = iElements[mutation.elementId];
      if (!iElement) return {};

      iElements[mutation.elementId] = {
        ...iElement,
        descr: mutation.value,
      };

      return { addedIElements: [iElement.id] };
    }
    case MutationTypes.MutationUserDirectoryMarkupField: {
      if (!mutation.elementId) return {};

      const iElement = iElements[mutation.elementId];
      if (!iElement || !isIElementMarkupAssignee(iElement)) return {};

      const newElement: IElementUserDirectoryMarkupField = {
        ...iElement,
        values: mutation.ids,
      };
      iElements[mutation.elementId] = newElement;

      return { addedIElements: [newElement.id] };
    }
    case MutationTypes.MutationDateTimeMarkupField: {
      if (!mutation.elementId) return {};

      const iElement = iElements[mutation.elementId];
      if (!iElement || !isIElementMarkupDueTime(iElement)) return {};

      const newElement: IElementDateTimeMarkupField = {
        ...iElement,
        value: mutation.dateTime,
      };
      iElements[mutation.elementId] = newElement;

      return { addedIElements: [newElement.id] };
    }
    case MutationTypes.MutationDropDownMarkupField: {
      if (!mutation.elementId) return {};

      const iElement = iElements[mutation.elementId];
      if (!iElement || !isIElementMarkupState(iElement)) return {};

      const newElement: IElementDropDownMarkupField = {
        ...iElement,
        value: mutation.value,
      };
      iElements[mutation.elementId] = newElement;

      return { addedIElements: [newElement.id] };
    }
    case MutationTypes.MutationDeleteElement: {
      return {};
    }
    default: {
      throw new Error(`${mutation.type}: Missing mutation type`);
    }
  }
}

/**
 * Compute the list of iElements that will be added or updated by a list of
 * mutations
 *
 * @param mutations The mutations that will be sent to the backend
 * @param rootId The ID of the root iElement of the project
 * @param userId The ID of the current user
 * @param date The date at which the iElement was changed
 * @param iElements The iElements currently saved in the store
 * @param newlyCreatedTags The list of tags that will be created by the mutations, but are not yet stored in the root object
 * @returns The list of iElements that will be updated by the input mutations
 */
export function mutationsToIElements(
  mutations: Mutation[],
  rootId: GUID,
  userId: GUID,
  date: string,
  iElements: IElementsRecord,
  newlyCreatedTags: ILabel[],
): {
  addedIElements?: IElement[];
  removedIElements?: GUID[];
} {
  const elementsToAdd: Set<GUID> = new Set<GUID>();
  const elementsToRemove: Set<GUID> = new Set<GUID>();
  mutations.forEach((m) => {
    const { addedIElements, removedIElements } = mutationToIElements(
      m,
      rootId,
      userId,
      date,
      iElements,
      newlyCreatedTags,
    );
    addedIElements?.forEach((id) => elementsToAdd.add(id));
    removedIElements?.forEach((id) => elementsToRemove.add(id));
  });
  return {
    addedIElements: [...elementsToAdd]
      .map((id) => iElements[id])
      .filter(isValid),
    removedIElements: [...elementsToRemove],
  };
}

type StoreUpdateFromMutations = (mutation: Mutation[]) => void;

/**
 * @returns A callback that allows to properly update the local store from
 * an input list of mutations
 */
export function useUpdateStoreFromMutations(): StoreUpdateFromMutations {
  const { getState } = useAppStore();
  const rootId = useAppSelector(selectRootId);
  const user = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();

  return useCallback(
    (mutations: Mutation[]) => {
      if (!user || !rootId) return;

      const date = DateTime.now().toISO();
      const newlyCreatedTags = selectNewlyCreatedTags(
        mutations,
        date,
        rootId,
      )(getState());
      const iElements = mutationsToIElements(
        mutations,
        rootId,
        user.id,
        date,
        {
          ...getState().iElements.iElements,
        },
        newlyCreatedTags,
      );
      if (iElements.addedIElements) {
        addIElementsToStore(
          updateHierarchy(
            iElements.addedIElements,
            getState().iElements.iElements,
          ),
          dispatch,
        );
      }
      iElements.removedIElements?.map((id) =>
        removeIElementFromStore(id, dispatch),
      );
    },
    [dispatch, getState, rootId, user],
  );
}

export type MutationTaskData = {
  /** The id of the mutation task that has completed */
  taskId: GUID;
  /** The id of the IElement which the mutation is related to */
  targetIElementId: GUID;
};

type UpdateProjectAfterMutationsCallback = (
  tasksData: MutationTaskData[],
) => Promise<void>;

/**
 * @returns A callback to update the local store if all the mutations related to a specific
 * target element has been processed
 */
export function useUpdateStoreAfterMutations(): UpdateProjectAfterMutationsCallback {
  const { getState } = useAppStore();
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();

  return useCallback<UpdateProjectAfterMutationsCallback>(
    async (tasksData: MutationTaskData[]): Promise<void> => {
      const ancestorsToLoad: Set<GUID> = new Set<GUID>();
      for (const { taskId, targetIElementId } of tasksData) {
        // If there are running mutations on the target element, wait
        // for everything to be completed to avoid updating the element with old data
        const tasks = selectBackgroundTasks(
          (t) =>
            t.type === "Mutation" &&
            t.rootId === targetIElementId &&
            t.id !== taskId,
        )(getState());

        if (tasks.length === 0) {
          ancestorsToLoad.add(targetIElementId);
        }

        dispatch(removeBackgroundTask(taskId));
      }

      if (ancestorsToLoad.size > 0) {
        // Update the IElement tree
        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: {
              // We only need to fetch the subtree starting from the targetIElement
              ancestorIds: [...ancestorsToLoad],
            },
          }),
        );
      }
    },
    [dispatch, getState, projectApiClient],
  );
}

/**
 * This selector computes the tags that will be created by the mutations. They are stored in the tags slice,
 * however they are not yet stored in the root object.
 *
 * @param mutations List of mutations of which we predict the update
 * @param date current date
 * @param rootId ID of project root element
 * @returns A selector to compute the tags that will be created by the mutations, that are not yet stored in the root object.
 */
function selectNewlyCreatedTags(
  mutations: Mutation[],
  date: ISOTimeString,
  rootId: GUID,
) {
  return (state: RootState): ILabel[] => {
    const rootLabels: ILabel[] =
      state.iElements.iElements[rootId]?.labels ?? [];

    return mutations.reduce<ILabel[]>((labels, mutation: Mutation) => {
      if (mutation.type !== MutationTypes.MutationAddLabel) {
        return labels;
      }
      const { labelId } = mutation;
      const label = rootLabels.find((l) => l.id === labelId);
      if (label) return labels;
      const tag = state.tags.tags.find((t) => t.id === labelId);
      assert(
        tag,
        "Tag should be found in the store if it's not present among root labels",
      );
      return [
        ...labels,
        {
          id: tag.id,
          name: tag.name,
          labelType: tag.labelType,
          createdAt: date,
          createdBy: state.user.user?.id ?? "",
          resourceId: state.iElements.projectId ?? "",
          lastModifiedInDb: null,
        },
      ];
    }, []);
  };
}
